<script lang="ts">
  import { faMousePointer,faPencilAlt,faStopwatch,faUserCircle } from '@fortawesome/free-solid-svg-icons';
  import Fa from 'svelte-fa';
  import { Badge,Button,Card,CardBody,CardHeader,Col,Modal,ModalBody,ModalFooter,ModalHeader,Row,Tooltip } from 'sveltestrap';
  import Skills from '../components/Skills.svelte';
  import type Headline from '../model/headline';
  import type Journalist from '../model/journalist';
  import { gamestate } from '../stores';
  import StarRating from './StarRating.svelte';

  export let tip: Headline;

  let isModalOpen = false;
  function toggle() {
    isModalOpen = !isModalOpen;
  }

  let modalHeadline: Headline;
  function showModal(headline: Headline) {
    modalHeadline = headline;
    isModalOpen = true;
  }

  let areYouSureOpen = false;
  let areYouSureJournalist: Journalist;
</script>

<Col lg="12" sm="12" class="d-flex justify-content-evenly">
  <span class:opacity-75="{tip.inProgress}" class="w-100">
    <Card class="mb-3 position-relative w-100">
      <CardHeader>
        <strong>{tip.title}</strong>
      </CardHeader>

      <CardBody>
        {#if tip.inProgress}
          {#each $gamestate.journalists as journalist}
            {#if journalist.currentProject == tip}
              <div class="mb-3">
                <Fa icon={faUserCircle}></Fa>
                {journalist.firstName} {journalist.lastName}
              </div>
            {/if}
          {/each}
        {/if}

        {#if tip.topics.length}
          <div class="mb-3">
            {#each tip.topics as topic}
              <Badge class="me-1">{topic}</Badge>
            {/each}
          </div>
        {/if}

        <div class="mb-3">
          <StarRating stars={tip.quality}></StarRating>
        </div>
        <div>
          <StarRating stars={tip.clickbait} icon={faMousePointer}></StarRating>
        </div>
      </CardBody>

      <a class="stretched-link" href="#" on:click={() => showModal(tip)}></a>
    </Card>
  </span>
</Col>

<Modal isOpen={isModalOpen} size="xl" {toggle}>
  <ModalHeader {toggle}>
    <h2 class="m-0">{modalHeadline.title}</h2>
  </ModalHeader>

  <ModalBody>
    {#if modalHeadline.topics.length}
      <h4 class="mb-3">
        {#each modalHeadline.topics as topic}
          <Badge class="me-1">{topic}</Badge>
        {/each}
      </h4>
    {/if}
    <div class="mb-3 d-flex flex-row">
      <span class="me-3">
        <StarRating stars={modalHeadline.quality} />
      </span>
      {modalHeadline.quality} star article.
      {#if modalHeadline.quality < 3}
        Low quality articles can be written quickly, but don't generate much engagement.
      {:else if modalHeadline.quality < 5}
        High quality articles take longer to write, but generate more engagement.
      {:else}
        Fantastic articles take a long time to write, but can really drive engagement to your site.
      {/if}
    </div>
    <div class="d-flex flex-row">
      <span class="me-3">
        <StarRating stars={modalHeadline.clickbait} icon={faMousePointer} />
      </span>
      {#if modalHeadline.clickbait < 2}
        Boring headlines may not draw the attention they deserve.
      {:else if modalHeadline.clickbait < 4}
        Interesting headlines generate more clicks than boring ones, leading to more views, shares, and revenue.
      {:else}
        Clickbait headlines generate lots of clicks, but disappointed readers might not share the article.
      {/if}
    </div>
  </ModalBody>

  <ModalFooter class="justify-content-center">
    <Row class="flex-grow-1 align-items-stretch">
      {#each $gamestate.journalists as journalist}
        <Col lg="3" md="6" sm="12" class="mb-3">
          <Button
            color={journalist.currentProject ? "secondary" : "dark"}
            outline
            class="d-flex flex-column w-100 h-100 mb-3"
            on:click={() => {
              if (journalist.currentProject && journalist.currentProject !== tip) {
                areYouSureJournalist = journalist;
                areYouSureOpen = true;
              } else {
                $gamestate.startProject(modalHeadline, journalist);
                gamestate.update(x=>x);
                isModalOpen = false;
              }
            }}
          >
            <Fa icon={faUserCircle} size="5x"/>
            <div class="mt-1">
              {journalist.firstName} {journalist.lastName}
            </div>

            <Skills journalist={journalist} filter={modalHeadline.topics} />
            <span class="flex-grow-1"></span>

            <div>
              {#if journalist.getBoostedQuality(modalHeadline) !== modalHeadline.quality}
                <span id="quality-boost-{journalist.id}">
                  <StarRating stars={journalist.getBoostedQuality(modalHeadline)} />
                </span>
                <Tooltip
                  target="quality-boost-{journalist.id}"
                  placement="bottom"
                >
                  {journalist.firstName}'s knowledge of the topic will increase the article's quality
                </Tooltip>
              {/if}

              <span id="speed-boost-{journalist.id}">
                <Fa icon={faStopwatch} />
                {journalist.getBoostedSpeed(modalHeadline)} turns
              </span>
              <Tooltip
                target="speed-boost-{journalist.id}"
                placement="bottom"
              >
                {#if journalist.getBoostedSpeed(modalHeadline) !== Math.ceil(modalHeadline.quality / 2)}
                  {journalist.firstName}'s knowledge of the topic will decrease the number of turns it takes to write the article
                {/if}
              </Tooltip>
            </div>

            <div class="sans fs-5 mt-3">
              <Fa icon={faPencilAlt} class="me-1" />
              {#if journalist.currentProject}
                CHANGE ASSIGNMENT
              {:else}
                WRITE!
              {/if}
            </div>
          </Button>
        </Col>
      {/each}
    </Row>
  </ModalFooter>
</Modal>

<Modal isOpen={areYouSureOpen} toggle={() => areYouSureOpen = !areYouSureOpen}>
  <ModalHeader>
    <h2>Change Assignment?</h2>
  </ModalHeader>

  <ModalBody>
    {areYouSureJournalist.firstName} is already writing <strong>{areYouSureJournalist.currentProject.title}</strong>.
    Do you want them to quit writing it and write <strong>{tip.title}</strong> instead?
  </ModalBody>

  <ModalFooter>
    <Button
      color="dark"
      outline
      class="sans"
      on:click={() => areYouSureOpen = false}
    >CANCEL</Button>
    <Button
      color="dark"
      class="sans"
      on:click={() => {
        $gamestate.startProject(tip, areYouSureJournalist);
        gamestate.update(x=>x);
        areYouSureOpen = false;
        isModalOpen = false;
      }}
    >CHANGE ASSIGNMENT</Button>
  </ModalFooter>
</Modal>